import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/Auth";
import api from "../helpers/api";
import {
  Box,
  Button,
  CssBaseline,
  Typography,
  Stack,
  Card as MuiCard,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import AppTheme from "../shared-theme/AppTheme";
import { MuiTelInput, matchIsValidTel } from "mui-tel-input";

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: "auto",
  boxShadow:
    "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
  [theme.breakpoints.up("sm")]: {
    width: "450px",
  },
  ...theme.applyStyles("dark", {
    boxShadow:
      "hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px",
  }),
}));

const GradientBackground = styled("div")({
  position: "fixed",
  inset: 0,
  background: "radial-gradient(circle at top left, #041C2C 0%, #22143C 100%)",
  zIndex: -1,
});

const SettingsContainer = styled(Stack)(({ theme }) => ({
  minHeight: "100dvh",
  padding: theme.spacing(2),
  overflowY: "auto",
  position: "relative",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
}));

const Whatsapp: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshAuth } = useAuth();
  const [subscription, setSubscription] = useState<any>(null);
  const [value, setValue] = React.useState("");
  const [isValid, setIsValid] = React.useState(true);
  const [loading, setLoading] = useState(false);
  const [updateStatus, setUpdateStatus] = useState("");
  const [verificationStatus, setVerificationStatus] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");

  useEffect(() => {
    const fetchSubscription = async () => {
      const sub = await api.get("/user/subscription");
      setSubscription(sub.data.subscription);
    };
    fetchSubscription();
    if (user?.phoneNumber) {
      setValue(user.phoneNumber);
    }
  }, [user]);

  const handleChange = (newValue: string) => {
    setUpdateStatus("");
    setVerificationStatus("");
    setValue(newValue.replace(/\s+/g, ""));
    setIsValid(newValue === "" || matchIsValidTel(newValue));
  };

  const handleUpdatePhone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) {
      return;
    }
    setLoading(true);
    try {
      await api.put("/user", { id: user?.id, phoneNumber: value || null });
      await refreshAuth();
      setUpdateStatus(
        value ? "Phone number successfully updated" : "Phone number removed"
      );
    } catch (error) {
      setUpdateStatus("Failed to update phone number");
    } finally {
      setLoading(false);
    }
  };

  const handleSendVerification = async () => {
    try {
      setUpdateStatus("");
      await api.post("/whatsapp/sendverification", {
        phoneNumber: value,
      });
      setVerificationStatus("Successfully sent verification code");
    } catch (error: any) {
      if (error.status === 429) {
        setVerificationStatus(error.response.data.error);
        return;
      }
      setVerificationStatus(
        "Failed to send verification text, please try again later. If the problem persists contact support@event-spy.com"
      );
    }
  };

  const handleVerifyCode = async () => {
    try {
      await api.post("/whatsapp/verify", {
        verificationToken: parseInt(verificationCode),
      });
      refreshAuth();
      setUpdateStatus("Phone number successfully verified");
      setIsModalOpen(false);
    } catch {
      setVerificationStatus("Invalid verification code");
    }
  };

  if (!user) {
    return (
      <AppTheme>
        <CssBaseline enableColorScheme />
        <GradientBackground />
        <SettingsContainer>
          <Card>
            <Typography variant="h6">Loading...</Typography>
          </Card>
        </SettingsContainer>
      </AppTheme>
    );
  }

  return (
    <AppTheme>
      <>
        {/* Meta tags for SEO */}
        <Helmet>
          <title>WhatsApp Settings | Event Spy</title>
        </Helmet>
      </>
      <CssBaseline enableColorScheme />
      <GradientBackground />
      <SettingsContainer direction="column" justifyContent="space-between">
        <Card variant="outlined">
          <Typography
            component="h1"
            variant="h4"
            sx={{ width: "100%", fontSize: "clamp(2rem, 10vw, 2.15rem)" }}
          >
            WhatsApp Alerts
          </Typography>
          {subscription && Object.keys(subscription).length > 0 ? (
            <Box>
              <Typography>
                By entering your number you agree to receive Whatsapp Alerts
                from Event Spy.
              </Typography>
              <Typography sx={{ mt: 1 }}>
                You can remove your number at any time.
              </Typography>
              <Box sx={{ width: "100%" }}>
                <MuiTelInput
                  fullWidth
                  defaultCountry="US"
                  value={value}
                  onChange={handleChange}
                  error={!isValid && value !== ""}
                  helperText={
                    !isValid && value !== "" ? "Invalid phone number" : " "
                  }
                  sx={{ mt: 2, mb: 2 }}
                  FlagIconButtonProps={{
                    disableRipple: true,
                    size: "small",
                    sx: {
                      border: "none",
                      background: "transparent",
                    },
                  }}
                />
                {updateStatus && (
                  <Typography
                    color={
                      updateStatus.includes("Failed") ? "error" : "success"
                    }
                    sx={{
                      mt: -4,
                      mb: 2,
                      ml: 1,
                    }}
                  >
                    {updateStatus}
                  </Typography>
                )}
                {user.phoneVerified ||
                !user.phoneNumber ||
                user.phoneNumber !== value ? (
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    onClick={handleUpdatePhone}
                    disabled={
                      !isValid ||
                      loading ||
                      updateStatus !== "" ||
                      value === user?.phoneNumber
                    }
                    sx={{
                      "&.Mui-disabled": {
                        color: "black",
                        opacity: 0.7,
                        backgroundColor: "primary.main",
                      },
                    }}
                  >
                    {loading ? "Updating..." : "Update Contact Number"}
                  </Button>
                ) : (
                  <Box>
                    <Typography color="warning.main" sx={{ mb: 1 }}>
                      Your phone number has not yet been verified.
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => {
                        handleSendVerification();
                        setIsModalOpen(true);
                      }}
                      sx={{
                        "&.Mui-disabled": {
                          color: "black",
                          opacity: 0.7,
                          backgroundColor: "primary.main",
                        },
                      }}
                    >
                      Send Verification Code
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            <>
              <Typography variant="body1" sx={{ mt: 2 }}>
                WhatsApp Alerts are only available to Pro members
              </Typography>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={() => navigate("/plans")}
                sx={{ mt: 2 }}
              >
                Upgrade to Pro
              </Button>
            </>
          )}
          <Button
            fullWidth
            variant="outlined"
            onClick={() => navigate("/console")}
            sx={{ mt: 2 }}
          >
            Back to Console
          </Button>
        </Card>
      </SettingsContainer>

      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        sx={{
          "& .MuiDialog-paper": {
            width: "500px",
            maxWidth: "100%",
          },
        }}
      >
        <DialogTitle>Verify Phone Number</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Verification Code"
            value={verificationCode}
            onChange={(e) => {
              const input = e.target.value;
              if (/^\d{0,6}$/.test(input)) {
                setVerificationStatus("");
                setVerificationCode(input);
              }
            }}
            sx={{ mt: 2 }}
            helperText={verificationStatus || " "}
            error={verificationStatus.includes("Invalid")}
            InputLabelProps={{
              sx: {
                top: "-6px",
                "&.Mui-focused": {
                  top: "0px",
                },
                "&.MuiInputLabel-shrink": {
                  top: "-4px",
                  color: "white",
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <>
            <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleVerifyCode}
              disabled={verificationCode.length < 6}
              sx={{
                "&.Mui-disabled": {
                  color: "black",
                  opacity: 0.7,
                  backgroundColor: "primary.main",
                },
              }}
            >
              Submit
            </Button>
          </>
        </DialogActions>
      </Dialog>
    </AppTheme>
  );
};

export default Whatsapp;
